import styled from "@emotion/styled"
import { Dialog } from "@mui/material"
import Slider from "@mui/material/Slider"

const StyledSubscriptionProductDlg = styled(Dialog)`
  .MuiPaper-root {
    width: min(980px, calc(100vw - 28px));
    max-width: none;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 8px;
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    padding: 10px 22px 10px;
    max-height: min(74vh, 860px);
    overflow: auto;
  }

  .MuiDialogActions-root {
    padding: 8px 22px 18px;
    gap: 10px;
  }

  .dialog-title-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .dialog-cancel-btn,
  .dialog-submit-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .dialog-submit-btn {
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }

  .MuiFormControl-root {
    margin: 0 0 14px;
    width: 100%;

    &:last-of-type {
      margin: 0;
    }
  }

  .control-label {
    font-size: 12px;
    color: var(--cms-text-secondary);
    font-weight: 700;
  }

  .form-row-two-cols {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    min-height: 70px;

    .MuiFormControl-root,
    .MuiInput-root,
    .MuiTextField-root {
      width: 100%;
    }
  }

  .dialog-section-title {
    margin: 10px 0 8px;
    font-size: 14px;
    font-weight: 800;
    color: var(--cms-text-primary);
  }

  @media (max-width: 860px) {
    .form-row-two-cols {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`

const ProgramRankSlider = styled(Slider)({
  color: "#52af77",
  height: 8,
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "var(--cms-input-bg)",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "inherit",
    },
    "&:before": {
      display: "none",
    },
  },
  "& .MuiSlider-valueLabel": {
    lineHeight: 1.2,
    fontSize: 12,
    background: "unset",
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: "50% 50% 50% 0",
    backgroundColor: "#52af77",
    transformOrigin: "bottom left",
    transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
    "&:before": { display: "none" },
    "&.MuiSlider-valueLabelOpen": {
      transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
    },
    "& > *": {
      transform: "rotate(45deg)",
    },
  },
})

const StyledSubscriptionProduct = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px 24px;
  border-radius: 24px;
  background: var(--cms-surface-primary);

  .heading-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .page-title {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 0;
    color: var(--cms-text-secondary);
    font-size: 13px;
    font-weight: 500;
  }

  .new-product-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    min-height: 40px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }

  .page-surface {
    border: 1px solid var(--cms-border);
    border-radius: 18px;
    background: var(--cms-surface-secondary);
    box-shadow: var(--cms-shadow);
    padding: 14px;
  }

  .page-empty {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }
`

const StyledSubscriptionProductTable = styled.div`
  .empty-group-des {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }

  .MuiTableContainer-root {
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  }

  .MuiTableCell-head {
    background: var(--cms-table-head-bg);
    color: var(--cms-text-primary);
    font-weight: 700;
    font-size: 12px;
    border-bottom: 1px solid var(--cms-border);
  }

  .MuiTableCell-body {
    font-size: 13px;
    color: var(--cms-text-primary);
    border-bottom: 1px solid var(--cms-border);
    vertical-align: top;
  }

  .status-chip {
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    padding: 2px 8px;
  }

  .status-chip.published {
    color: #166534;
    background: #dcfce7;
  }

  .status-chip.unpublished {
    color: #b91c1c;
    background: #fee2e2;
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .row-actions .MuiButton-root {
    margin-left: 0;
    text-transform: none;
    border-radius: 9px;
    min-height: 34px;
    font-weight: 700;
    padding: 4px 10px;
  }
`

const StyledPlatformSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--cms-border);
  border-radius: 12px;
  background: var(--cms-surface-muted);
  padding: 10px;
  min-height: 120px;

  .platform-title-label {
    font-size: 13px;
    color: var(--cms-text-primary);
    font-weight: 700;
    margin: 2px 0 8px;
  }

  .platform-action-button {
    margin-top: 6px;
    margin-left: 0;
  }

  .platform-add-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;

    button {
      margin-right: 0;
      text-transform: none;
      border-radius: 8px;
      min-height: 32px;
      font-weight: 700;
      padding: 4px 10px;
    }
  }

  .platform-muted {
    color: var(--cms-text-secondary);
    font-size: 12px;
    margin-top: 8px;
    font-weight: 600;
  }

  .platform-tabs {
    border: 1px solid var(--cms-border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--cms-surface-secondary);
  }

  .platform-tabs .MuiTabs-root {
    min-height: 44px;
    border-bottom: 1px solid var(--cms-border);
    background: var(--cms-table-head-bg);
  }

  .platform-tabs .MuiTab-root {
    text-transform: none;
    font-weight: 700;
    min-height: 44px;
  }

  .platform-panel {
    padding: 12px;
  }

  .platform-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
  }
`

export {
  StyledSubscriptionProductDlg,
  StyledSubscriptionProduct,
  ProgramRankSlider,
  StyledSubscriptionProductTable,
  StyledPlatformSection,
}
